/// <amd-module name="Wit/HtmlEditor/Plugins/MentionPlugin" />

import { PasteOption } from "roosterjs-react";
import { Editor, EditorPlugin, ExtractContentEvent, PluginEvent, PluginEventType, SanitizeHtmlOptions, AttributeCallbackMap } from "roosterjs-react";
import { ObservableValue } from "VSS/Core/Observable";
import { IVssPageContext } from "VSS/Platform/Context";
import { convertRawMentionsToRichPreviews, revertMentions, sanitizeExternalLinks } from "../Utils/Mention";

export interface IMentionPlugin extends EditorPlugin {
    onFocus: () => void;
    onBlur: () => void;
}

/**
 * This plugin is for rendering HTML mentions in HTML fields. It renders mentions on blur, and unrenders them when you focus into edit mode.
 */
export class MentionPlugin implements IMentionPlugin {
    private _pageContext;
    private _editor: Editor | null;
    private _latestRenderTime: number;
    private _hasFocus: boolean;
    private _autocompleteIsSuggesting: ObservableValue<boolean>;

    constructor(pageContext: IVssPageContext, autocompleteIsSuggesting: ObservableValue<boolean>) {
        this._pageContext = pageContext;
        this._autocompleteIsSuggesting = autocompleteIsSuggesting;
    }

    public getName(): string {
        return "MentionPlugin";
    }

    public initialize(editor: Editor): void {
        this._editor = editor;
        this._hasFocus = false;
    }

    public onPluginEvent(event: PluginEvent): void {
        if (event.eventType === PluginEventType.BeforePaste && this._autocompleteIsSuggesting.value) {
            // Clear out the clipboardData and let the autocomplete control handle the paste.
            event.clipboardData.rawHtml = "";
            event.clipboardData.text = "";
            event.pasteOption = PasteOption.PasteText;
        }
        if (event.eventType === PluginEventType.EditorReady || event.eventType === PluginEventType.ContentChanged) {
            // When the content loads for the first time or when the content changes, show pretty mentions
            this._showRichMentionPreviews();
        } else if (event.eventType === PluginEventType.ExtractContent) {
            // When content is extracted from the editor, return the extracted content to its raw format
            const extractContentEvent = event as ExtractContentEvent;
            extractContentEvent.content = revertMentions(extractContentEvent.content);
        }
    }

    public dispose(): void {
        this._editor = null;
    }

    /**
     * This is not part of the EditorPlugin interface, so it must explicitly be bound to the editor.
     */
    public onBlur(): void {
        this._hasFocus = false;
    }

    /**
     * This is not part of the EditorPlugin interface, so it must explicitly be bound to the editor.
     */
    public onFocus(): void {
        this._hasFocus = true;
        this._showRawMentions();
    }

    public static SanitizeHtmlOptions = {
        attributeCallbacks: {
            class: (value: string, element: HTMLElement) => {
                const mentionElement = this._closest(element, "[data-rendered-mention]");
                return mentionElement ? value : undefined;
            },
            style: (value: string): string =>
                value
                    .replace(/(height|line-height)\s*:.+?(;[\s]?|$)/g, "") // remove height and line-height from inline style
                    .replace("background-color: rgb(255, 255, 255);", "") // remove white background for theming
                    .replace("color: rgb(0, 0, 0);", ""), // remove black text for theming

            color: (value: string): string | undefined => {
                return undefined;
            },

            ["data-vss-mention"]: (value: string, element: HTMLElement) => value,
            ["data-rendered-mention"]: (value: string, element: HTMLElement) => value
        } as AttributeCallbackMap
    } as SanitizeHtmlOptions;

    private async _showRichMentionPreviews(): Promise<void> {
        const latestContent = this._getEditorContentWithRawMentions();
        const currentRenderTime = (this._latestRenderTime = Date.now());

        if (!latestContent || latestContent.indexOf("data-vss-mention") === -1) {
            // There are no mentions, so there is no work to do.
            return;
        }

        // Convert the stringified content to an html document
        const contentElement = document.createElement("div");
        contentElement.innerHTML = latestContent;

        sanitizeExternalLinks(contentElement);
        await convertRawMentionsToRichPreviews(contentElement, this._pageContext);

        if (currentRenderTime !== this._latestRenderTime || this._hasFocus) {
            // Since we started showing the mentions, we have either requested mentions again,
            // or the editor has gained focus. In either case, we should not proceed with
            // seting the editor content.
            return;
        }

        this._setEditorContentWithoutChangeEvent(contentElement.innerHTML);
    }

    private _showRawMentions(): void {
        this._setEditorContentWithoutChangeEvent(this._getEditorContentWithRawMentions());
    }

    // Returns exact contents of the editor (including temporary plugin data) without rendering Mentions
    private _getEditorContentWithRawMentions(): string {
        if (!this._editor) {
            return "";
        }
        return revertMentions(this._editor.getContent(false));
    }

    private _setEditorContentWithoutChangeEvent(content: string): void {
        if (!this._editor) {
            return;
        }
        this._editor.setContent(content, false /* triggerContentChangedEvent */);
    }

    private static _closest(element: Element, query: string): Element | null {
        if (element && element.closest) {
            return element.closest(query);
        }

        // for IE11 and below
        while (element && element.parentElement && !(element.matches || (element as any).msMatchesSelector).call(element, query)) {
            element = element.parentElement;
        }

        return element;
    }
}
